// Configuration centralisee - a utiliser dans TOUS les composants
const hostname = window.location.hostname;
const isLoopback = hostname === 'localhost' ||
                   hostname === '127.0.0.1' ||
                   hostname === '';
const isPrivateNetwork = /^(10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.|192\.168\.|0\.0\.0\.0$)/.test(hostname);
const isLocal = isLoopback || isPrivateNetwork;

const localBackendHost = isLoopback ? 'localhost' : hostname;
const localUrl = `http://${localBackendHost}/crou_niger_projet/backend_php`;
const productionUrl = 'https://univtopdosso.com/backend_php';

const baseUrl = isLocal ? localUrl : productionUrl;
const productionVideoStudioBase = 'http://app.univtopdosso.com/api';
const videoStudioBase = window.__VIDEO_STUDIO_BASE__ ||
    process.env.REACT_APP_VIDEO_STUDIO_BASE ||
    (isLocal ? `http://${localBackendHost}:4000/api` : productionVideoStudioBase);
const uploadsPathMarker = '/uploads/';

export const config = {
    apiBase: `${baseUrl}/api`,
    videoStudioBase,
    uploadsBase: `${baseUrl}/uploads`,

    // ✅ Dossiers de stockage
    folders: {
        videos: 'videos',
        qr_codes: 'qr_codes',
        profils: 'profils',
        posts: 'posts',
        applications: 'applications',
        bus: 'bus',
        documents: 'documents',
        messagerie: 'messagerie',
        recus: 're\u00e7us',
        ['re\u00e7us']: 're\u00e7us',
        cours_medias: 'cours_medias',
        identites_pat: 'identites_pat',
        identites: 'identites',
        qr_codes_pat: 'qr_codes_pat',
        programmes_tv: 'programmes_tv',
        statuts: 'statuts',
        boutiques: 'boutiques',
        produits: 'boutiques/produits'
    },

    // ✅ Fonction principale de normalisation des URLs
    normalizeUrl(url, typeHint = null) {
        if (!url) return '';

        const raw = String(url).trim();
        if (!raw) return '';
        if (raw.startsWith('data:') || raw.startsWith('blob:')) return raw;

        const cleaned = raw.replace(/\\/g, '/');
        const buildUploadUrl = (relative, suffix = '') => {
            const cleanRelative = String(relative || '').replace(/^\/+/, '');
            return cleanRelative ? `${this.uploadsBase}/${cleanRelative}${suffix}` : '';
        };

        const uploadIndex = cleaned.indexOf(uploadsPathMarker);
        if (uploadIndex !== -1 && !cleaned.startsWith('http://') && !cleaned.startsWith('https://')) {
            return buildUploadUrl(cleaned.slice(uploadIndex + uploadsPathMarker.length));
        }

        if (cleaned.startsWith('/uploads/')) {
            return buildUploadUrl(cleaned.slice('/uploads/'.length));
        }

        if (cleaned.startsWith('uploads/')) {
            return buildUploadUrl(cleaned.slice('uploads/'.length));
        }

        if (cleaned.startsWith('http://') || cleaned.startsWith('https://')) {
            try {
                const parsed = new URL(cleaned);
                const markerIndex = parsed.pathname.indexOf(uploadsPathMarker);
                if (markerIndex !== -1) {
                    const relative = parsed.pathname.slice(markerIndex + uploadsPathMarker.length);
                    return buildUploadUrl(relative, parsed.search + parsed.hash);
                }
            } catch (error) {
                return cleaned;
            }
            return cleaned;
        }

        for (const folder of Object.values(this.folders)) {
            if (cleaned.startsWith(folder + '/') || cleaned.includes('/' + folder + '/')) {
                return buildUploadUrl(cleaned);
            }
        }

        if (typeHint && this.folders[typeHint]) {
            return buildUploadUrl(`${this.folders[typeHint]}/${cleaned}`);
        }

        return buildUploadUrl(`posts/${cleaned}`);
    },

    // ✅ Fonctions de normalisation par type
    normalizeProfilePhoto(url) {
        return this.normalizeUrl(url, 'profils');
    },
    normalizePostMedia(url) {
        return this.normalizeUrl(url, 'posts');
    },
    normalizeQrCode(url) {
        return this.normalizeUrl(url, 'qr_codes');
    },
    normalizeBusPhoto(url) {
        return this.normalizeUrl(url, 'bus');
    },
    normalizeDocument(url) {
        return this.normalizeUrl(url, 'documents');
    },
    normalizeRecu(url) {
        return this.normalizeUrl(url, 'recus');
    },
    ['normalizeRe\u00e7u'](url) {
        return this.normalizeRecu(url);
    },
    normalizeMessageMedia(url) {
        return this.normalizeUrl(url, 'messagerie');
    },
    normalizeVideo(url) {
        return this.normalizeUrl(url, 'videos');
    },
    normalizeCourseMedia(url) {
        return this.normalizeUrl(url, 'cours_medias');
    },
    normalizeApplication(url) {
        return this.normalizeUrl(url, 'applications');
    },
    normalizeStatutMedia(url) {
        return this.normalizeUrl(url, 'statuts');
    },
    normalizeBoutiqueMedia(url) {
        return this.normalizeUrl(url, 'boutiques');
    },
    normalizeProduitMedia(url) {
        return this.normalizeUrl(url, 'produits');
    },

    // ✅ Fonctions spécifiques pour les cours
    getCourseVideoUrl(filename) {
        if (!filename) return null;
        if (filename.startsWith('http://') || filename.startsWith('https://')) {
            return filename;
        }
        if (filename.startsWith('/')) {
            return `${this.uploadsBase}${filename}`;
        }
        return `${this.uploadsBase}/cours_medias/${filename}`;
    },

    getCourseImageUrl(filename) {
        if (!filename) return null;
        if (filename.startsWith('http://') || filename.startsWith('https://')) {
            return filename;
        }
        if (filename.startsWith('/')) {
            return `${this.uploadsBase}${filename}`;
        }
        return `${this.uploadsBase}/cours_medias/${filename}`;
    },

    // ✅ Fonctions pour les boutiques
    getBoutiquePhotoUrl(filename) {
        if (!filename) return null;
        if (filename.startsWith('http://') || filename.startsWith('https://')) {
            return filename;
        }
        if (filename.startsWith('/')) {
            return `${this.uploadsBase}${filename}`;
        }
        return `${this.uploadsBase}/boutiques/${filename}`;
    },

    getProduitPhotoUrl(filename) {
        if (!filename) return null;
        if (filename.startsWith('http://') || filename.startsWith('https://')) {
            return filename;
        }
        if (filename.startsWith('/')) {
            return `${this.uploadsBase}${filename}`;
        }
        return `${this.uploadsBase}/boutiques/produits/${filename}`;
    },

    chunkSize: 5 * 1024 * 1024,
    isLocal,
    baseUrl,
    
    // ✅ URL de base pour les boutiques
    boutiqueApiBase: `${baseUrl}/api/boutiques`
};

export default config;